import { useState, useEffect, useRef, useMemo } from 'react';
import { Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
  fetchNhoHeaders,
  createNhoQuestion,
  updateNhoQuestion,
  patchNhoStatus,
  CreateNhoQuestionPayload,
  UpdateNhoQuestionPayload,
} from '../../../../store/slices/adminSlice';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  THREE_PL_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  NHO_TOUCH_TYPE_OPTIONS,
  NHO_SECTION_OPTIONS,
} from './kfaDropdownOptions';
import { getValidationState, REQUIRED_FIELDS_MESSAGE } from './validationUtils';
import { MSOption } from './KfaMultiSelectDropdown';
import { SDOption } from './NhoSearchableDropdown';
import { NhoEditItem } from './NhoAddOverlay';

export interface NhoValidationErrors {
  touchTypes: boolean;
  section: boolean;
  sortOrder: boolean;
  qtext: boolean;
  header: boolean;
  subHeader: boolean;
  locationType: boolean;
  organizationType: boolean;
  threeplStatus: boolean;
  category: boolean;
}

export const ASSESSMENT_LABEL_MAP: Record<string, string> = {
  Header: 'Title',
  'Sub-Header': 'Sub-Data',
  Data: 'Key Focus Area',
  'Sub-Data': 'Data',
};

const normalizeTouchTypeId = (value: string): string => String(value).trim().toLowerCase();

const hasTouchTypeMatch = (item: NhoEditItem | any, selectedTouchTypeIds: Set<string>): boolean => {
  if (selectedTouchTypeIds.size === 0) return false;

  const probation30 = Number(item?.probation30 ?? 0) === 1;
  const probation60 = Number(item?.probation60 ?? 0) === 1;
  const probation90 = Number(item?.probation90 ?? 0) === 1;
  const preferredMethod = Number(item?.preferredMethod ?? 0) === 1;

  if (selectedTouchTypeIds.has('probation_30') && probation30) return true;
  if (selectedTouchTypeIds.has('probation_60') && probation60) return true;
  if (selectedTouchTypeIds.has('probation_90') && probation90) return true;
  if (selectedTouchTypeIds.has('preferred_method') && preferredMethod) return true;

  if (Array.isArray(item?.touchTypes)) {
    const normalizedItemTouchTypes = new Set(item.touchTypes.map((touchType: string) => normalizeTouchTypeId(touchType)));
    for (const selectedId of selectedTouchTypeIds) {
      if (normalizedItemTouchTypes.has(selectedId)) return true;
    }
  }

  return false;
};

const findOption = <T extends { id: string | number; displayText: string }>(
  options: T[],
  value: string | number | undefined | null,
): T | null => {
  if (value === undefined || value === null || value === '') return null;
  return options.find((o) => String(o.id).toLowerCase() === String(value).toLowerCase()) ?? null;
};

const splitCsv = (value: string | undefined | null): string[] => {
  if (!value) return [];
  return String(value).split(',').map((item) => item.trim()).filter(Boolean);
};

const EMPTY_ERRORS: NhoValidationErrors = {
  touchTypes: false,
  section: false,
  sortOrder: false,
  qtext: false,
  header: false,
  subHeader: false,
  locationType: false,
  organizationType: false,
  threeplStatus: false,
  category: false,
};

interface UseNhoFormParams {
  visible: boolean;
  mode: 'create' | 'edit';
  selectedItem?: NhoEditItem;
  onDismiss: () => void;
  onSaveSuccess?: () => void;
}

export function useNhoForm({ visible, mode, selectedItem, onDismiss, onSaveSuccess }: UseNhoFormParams) {
  const dispatch = useDispatch<AppDispatch>();
  const nhoData = useSelector((s: RootState) => s.configuration.nhoQuestions.headers);
  const headersLoaded = useSelector((s: RootState) => s.configuration.nhoQuestions.headersLoaded);
  const [touchTypes, setTouchTypes] = useState<MSOption[]>([]);

  useEffect(() => {
    if (visible && !headersLoaded) {
      dispatch(fetchNhoHeaders());
    }
  }, [visible, headersLoaded, dispatch]);

  const allHeaderOptions = useMemo<SDOption[]>(() => {
    const headers = nhoData.filter((item) => item.sectionType === 'Header');
    const unique = Array.from(
      new Map(headers.map((item) => [item.sectionValue, item])).values(),
    );
    return unique.map((item) => ({
      id: String(item.id),
      displayText: (item.sectionValue ?? String(item.id)).replace(/:/g, '').trim(),
    }));
  }, [nhoData]);

  const subHeaderOptions = useMemo<SDOption[]>(() => {
    const subHeaders = nhoData.filter(
      (item) => item.sectionType === 'Sub-Header' || item.sectionType === 'Sub-header',
    );
    const unique = Array.from(
      new Map(subHeaders.map((item) => [item.sectionValue, item])).values(),
    );
    return unique.map((item) => ({
      id: String(item.id),
      displayText: item.sectionValue ?? String(item.id),
      parentId: item.parentId != null ? String(item.parentId) : undefined,
    }));
  }, [nhoData]);

  const headerIdToOption = useMemo<Map<string, SDOption>>(() => {
    const svToOption = new Map<string, SDOption>(
      allHeaderOptions.map((opt) => [opt.displayText, opt]),
    );
    const map = new Map<string, SDOption>();
    nhoData
      .filter((item) => item.sectionType === 'Header')
      .forEach((item) => {
        const sv = (item.sectionValue ?? String(item.id)).replace(/:/g, '').trim();
        const canonical = svToOption.get(sv);
        if (canonical) map.set(String(item.id), canonical);
      });
    return map;
  }, [nhoData, allHeaderOptions]);

  const filteredHeaderOptions = useMemo<SDOption[]>(() => {
    const selectedTouchTypeIds = new Set(
      touchTypes.map((touchType) => normalizeTouchTypeId(String(touchType.id))).filter(Boolean),
    );
    const matchingHeaders = nhoData.filter((item) => {
      const isHeader = item.sectionType === 'Header';
      return isHeader && hasTouchTypeMatch(item, selectedTouchTypeIds);
    });

    const unique = Array.from(
      new Map(matchingHeaders.map((item) => [
        (item.sectionValue ?? String(item.id)).replace(/:/g, '').trim(),
        item,
      ])).values(),
    );

    return unique.map((item) => ({
      id: String(item.id),
      displayText: (item.sectionValue ?? String(item.id)).replace(/:/g, '').trim(),
    }));
  }, [nhoData, touchTypes]);

  // Map from header ID to its section value
  const headerIdToSectionMap = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    nhoData
      .filter((item) => item.sectionType === 'Header')
      .forEach((item) => {
        if (item.section !== undefined && item.section !== null) {
          map.set(String(item.id), item.section);
        }
      });
    return map;
  }, [nhoData]);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Form state
  const [section, setSection] = useState<any>(null);
  const [sectionNumber, setSectionNumber] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('');
  const [qtext, setQtext] = useState('');
  const [selectedHeader, setSelectedHeader] = useState<SDOption | null>(null);
  const [selectedSubHeader, setSelectedSubHeader] = useState<SDOption | null>(null);
  const [locationType, setLocationType] = useState<MSOption[]>([]);
  const [organizationType, setOrganizationType] = useState<MSOption[]>([]);
  const [threeplStatus, setThreeplStatus] = useState<any>(null);
  const [category, setCategory] = useState<MSOption[]>([]);
  const [statusEnabled, setStatusEnabled] = useState<boolean>(true);

  const isTextLocked = mode === 'edit' && !!selectedItem?.createdAt
    && (Date.now() - new Date(selectedItem.createdAt).getTime()) > 24 * 60 * 60 * 1000;

  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<NhoValidationErrors>({ ...EMPTY_ERRORS });
  const isDirtyRef = useRef(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Derived flags
  const sectionId = String(section?.id ?? '');
  const assessmentLabel = sectionId ? (ASSESSMENT_LABEL_MAP[sectionId] ?? 'Title') : 'Title';
  const showSubHeaderDropdown = sectionId === 'Sub-Data';
  const showHeaderDropdown = sectionId === 'Sub-Header' || sectionId === 'Data' || sectionId === 'Sub-Data';
  const sideBySideLayout = showSubHeaderDropdown && showHeaderDropdown;

  const clearFieldError = (field: keyof NhoValidationErrors) => {
    isDirtyRef.current = true;
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
    setValidationMessage(null);
  };

  const validateForm = () => {
    const requiresHeader = sectionId === 'Sub-Header' || sectionId === 'Data' || sectionId === 'Sub-Data';
    const requiresSubHeader = sectionId === 'Sub-Data';
    const { fieldErrors: nextErrors, hasErrors } = getValidationState<keyof NhoValidationErrors>({
      touchTypes: touchTypes.length === 0,
      section: !section,
      sortOrder: !sortOrder.trim(),
      qtext: !qtext.trim(),
      header: requiresHeader && !selectedHeader,
      subHeader: requiresSubHeader && !selectedSubHeader,
      locationType: locationType.length === 0,
      organizationType: organizationType.length === 0,
      threeplStatus: !threeplStatus,
      category: category.length === 0,
    });
    setFieldErrors(nextErrors);
    if (hasErrors) {
      setValidationMessage(REQUIRED_FIELDS_MESSAGE);
      return false;
    }
    return true;
  };

  const handleSectionChange = (newSection: any) => {
    setSection(newSection);
    setSelectedHeader(null);
    setSelectedSubHeader(null);
  };

  const handleSubHeaderChange = (opt: SDOption | null) => {
    setSelectedSubHeader(opt);
    if (opt?.parentId) {
      const linkedHeader = headerIdToOption.get(opt.parentId) ?? null;
      if (linkedHeader) setSelectedHeader(linkedHeader);
    }
  };

  // Scroll-lock (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (visible) {
      const scrollY = (window as any).scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      (document.body.dataset as any).nhoOverlayScrollY = String(scrollY);
    } else {
      const savedY = parseInt((document.body.dataset as any).nhoOverlayScrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedY);
    }
    return () => {
      if (Platform.OS === 'web') {
        const savedY = parseInt((document.body.dataset as any).nhoOverlayScrollY || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, savedY);
      }
    };
  }, [visible]);

  // Reset / Pre-fill on visibility change
  useEffect(() => {
    if (!visible) {
      setTouchTypes([]);
      setSection(null);
      setSectionNumber('');
      setSortOrder('');
      setQtext('');
      setSelectedHeader(null);
      setSelectedSubHeader(null);
      setLocationType([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setCategory([]);
      setStatusEnabled(true);
      setSaveError(null);
      setValidationMessage(null);
      setFieldErrors({ ...EMPTY_ERRORS });
      isDirtyRef.current = false;
      return;
    }

    if (mode === 'edit' && selectedItem) {
      const baseTouchTypes = (selectedItem.touchTypes ?? []).filter((type) => type !== 'Preferred_Method');
      const hasPreferredMethod = Number(selectedItem.preferredMethod ?? 0) === 1;
      const touchTypeValues = hasPreferredMethod
        ? [...new Set([...baseTouchTypes, 'Preferred_Method'])]
        : baseTouchTypes;

      setTouchTypes(
        touchTypeValues.map((t) => {
          const opt = NHO_TOUCH_TYPE_OPTIONS.find((o) => String(o.id) === t);
          return { id: t, displayText: opt?.displayText ?? t };
        }),
      );
      setSection(findOption(NHO_SECTION_OPTIONS, selectedItem.sectionType) ?? findOption(NHO_SECTION_OPTIONS, 'Data'));
      setSectionNumber(selectedItem.section != null ? String(selectedItem.section) : '');
      setSortOrder(selectedItem.sortOrder != null ? String(selectedItem.sortOrder) : '');
      setQtext(selectedItem.qtext ?? '');

      const normalize = (s: string) => s.replace(/:/g, '').trim().toLowerCase();
      setSelectedHeader(
        selectedItem.sectionHeader
          ? (filteredHeaderOptions.find((h) => normalize(h.displayText) === normalize(selectedItem.sectionHeader!)) ??
             allHeaderOptions.find((h) => normalize(h.displayText) === normalize(selectedItem.sectionHeader!)) ??
             null)
          : null,
      );
      setSelectedSubHeader(
        selectedItem.sectionSubHeader
          ? (subHeaderOptions.find((s) => normalize(s.displayText) === normalize(selectedItem.sectionSubHeader!)) ?? null)
          : null,
      );

      const locationValues = splitCsv(selectedItem.locationType).filter((value) => value !== 'Preferred_Method');
      setLocationType(
        LOCATION_TYPE_OPTIONS.filter((option) =>
          locationValues.some((value) => String(option.id).toLowerCase() === String(value).toLowerCase()),
        ),
      );
      const organizationValues = splitCsv(selectedItem.organizationType);
      setOrganizationType(
        ORGANIZATION_TYPE_OPTIONS.filter((option) =>
          organizationValues.some((value) => String(option.id).toLowerCase() === String(value).toLowerCase()),
        ),
      );
      setThreeplStatus(
        selectedItem.threePlStatus !== undefined
          ? findOption(THREE_PL_OPTIONS, selectedItem.threePlStatus === 'YES' ? 'Yes' : 'No')
          : null,
      );
      const categoryValues = splitCsv(selectedItem.category);
      setCategory(
        SPARC_CATEGORY_OPTIONS.filter((option) =>
          categoryValues.some((value) => String(option.id).toLowerCase() === String(value).toLowerCase()),
        ),
      );
      setStatusEnabled(selectedItem.status !== false);
    } else {
      setTouchTypes([]);
      setSection(null);
      setSectionNumber('');
      setSortOrder('');
      setQtext('');
      setSelectedHeader(null);
      setSelectedSubHeader(null);
      setLocationType([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setCategory([]);
      setStatusEnabled(true);
      setSaveError(null);
      setValidationMessage(null);
      setFieldErrors({ ...EMPTY_ERRORS });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode, selectedItem]);

  // Late-load guard: re-fill header/sub-header once options arrive
  useEffect(() => {
    if (!visible || mode !== 'edit' || !selectedItem) return;
    const normalize = (s: string) => s.replace(/:/g, '').trim().toLowerCase();
    if (selectedItem.sectionHeader && !selectedHeader && filteredHeaderOptions.length > 0) {
      const found = filteredHeaderOptions.find((h) => normalize(h.displayText) === normalize(selectedItem.sectionHeader!)) ??
        allHeaderOptions.find((h) => normalize(h.displayText) === normalize(selectedItem.sectionHeader!)) ??
        null;
      if (found) setSelectedHeader(found);
    }
    if (selectedItem.sectionSubHeader && !selectedSubHeader && subHeaderOptions.length > 0) {
      const found = subHeaderOptions.find((s) => normalize(s.displayText) === normalize(selectedItem.sectionSubHeader!)) ?? null;
      if (found) setSelectedSubHeader(found);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredHeaderOptions, subHeaderOptions]);

  // Payload builders
  const buildItemLevelFields = () => {
    const selectedTouchTypeIds: string[] = touchTypes.map((t) => String(t.id)).filter(Boolean);
    const hasPreferredMethod = selectedTouchTypeIds.includes('Preferred_Method');
    const touchTypesPayload: string[] = selectedTouchTypeIds.filter((type) => type !== 'Preferred_Method');
    const locationTypesPayload: string[] = locationType
      .map((x) => String(x.id))
      .filter((value) => value && value !== 'Preferred_Method');
    const resolvedSectionHeader: string | null =
      sectionId === 'Header'
        ? qtext.trim() || null
        : selectedHeader?.displayText ?? null;
    const resolvedSectionSubHeader: string | null =
      sectionId === 'Sub-Header'
        ? qtext.trim() || null
        : sectionId === 'Sub-Data'
        ? selectedSubHeader?.displayText ?? null
        : null;
    return {
      touchTypes: touchTypesPayload,
      locationTypes: locationTypesPayload,
            section: sectionId === 'Header' 
        ? (sectionNumber !== '' ? parseInt(sectionNumber, 10) || 0 : 0)
        : (sectionId === 'Sub-Header' || sectionId === 'Data') && selectedHeader
          ? (headerIdToSectionMap.get(selectedHeader.id) ?? 0)
        : (sectionId === 'Sub-Data') && selectedSubHeader
        ? (headerIdToSectionMap.get(selectedSubHeader.id) ?? 0)
        : 0,
      preferredMethod: hasPreferredMethod ? 1 : 0,
      status: statusEnabled,
      organizationType: organizationType.length > 0 ? organizationType.map((x) => String(x.id)).join(',') : null,
      threePlStatus: threeplStatus?.id === 'Yes' ? true : threeplStatus?.id === 'No' ? false : null,
      category: category.length > 0 ? category.map((x) => String(x.id)).join(',') : null,
      sectionHeader: resolvedSectionHeader,
      sectionSubHeader: resolvedSectionSubHeader,
    };
  };

  const toSafeInt = (value: string): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  };

  const buildPostPayload = (): Omit<CreateNhoQuestionPayload, 'userEmail' | 'gpid'> => {
    const fields = buildItemLevelFields();
    const sortOrderInt = toSafeInt(sortOrder);
    switch (sectionId) {
      case 'Header':
        return { header: { sectionValue: qtext.trim(), ...fields }, items: [], sortOrder: sortOrderInt };
      case 'Sub-Header':
        return { existingHeaderId: selectedHeader ? Number(selectedHeader.id) : undefined, items: [{ sectionType: 'Sub-Header', sectionValue: qtext.trim(), ...fields }], sortOrder: sortOrderInt };
      case 'Data':
        return { existingHeaderId: selectedHeader ? Number(selectedHeader.id) : undefined, items: [{ sectionType: 'Data', sectionValue: qtext.trim(), ...fields }], sortOrder: sortOrderInt };
      case 'Sub-Data':
      default:
        return { existingSubHeaderId: selectedSubHeader ? Number(selectedSubHeader.id) : undefined, subItems: [{ sectionValue: qtext.trim(), ...fields }], sortOrder: sortOrderInt };
    }
  };

  const buildPutPayload = (): Omit<UpdateNhoQuestionPayload, 'userEmail' | 'gpid'> => {
    const fields = buildItemLevelFields();
    const sortOrderInt = toSafeInt(sortOrder);
    const common = { sectionType: sectionId, sectionValue: qtext.trim(), sortOrder: sortOrderInt, ...fields };
    switch (sectionId) {
      case 'Sub-Data':
        return { ...common, existingSubHeaderId: selectedSubHeader ? Number(selectedSubHeader.id) : undefined };
      case 'Sub-Header':
      case 'Data':
        return { ...common, existingHeaderId: selectedHeader ? Number(selectedHeader.id) : undefined };
      case 'Header':
      default:
        return common;
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setValidationMessage(null);
    setSaveError(null);
    try {
      if (mode === 'edit' && selectedItem?.id) {
        const payload = buildPutPayload();
        console.log('[NHO PUT] id/payload →', selectedItem.id, JSON.stringify(payload));
        await dispatch(updateNhoQuestion({ id: selectedItem.id, ...payload })).unwrap();
        await dispatch(patchNhoStatus({ id: selectedItem.id, status: statusEnabled })).unwrap();
      } else {
        const payload = buildPostPayload();
        console.log('[NHO POST] payload →', JSON.stringify(payload));
        await dispatch(createNhoQuestion(payload)).unwrap();
      }
      onDismiss();
      onSaveSuccess?.();
    } catch (_err: any) {
      setSaveError((_err as any)?.message ?? 'Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true);
    } else {
      onDismiss();
    }
  };

  return {
    // Redux-derived options
    headerOptions: filteredHeaderOptions,
    subHeaderOptions,
    headersLoaded,
    // Save state
    isSaving,
    saveError,
    // Form state
    touchTypes, setTouchTypes,
    section, setSection,
    sectionNumber, setSectionNumber,
    sortOrder, setSortOrder,
    qtext, setQtext,
    selectedHeader, setSelectedHeader,
    selectedSubHeader,
    locationType, setLocationType,
    organizationType, setOrganizationType,
    threeplStatus, setThreeplStatus,
    category, setCategory,
    statusEnabled, setStatusEnabled,
    isTextLocked,
    validationMessage,
    fieldErrors,
    isDirtyRef,
    showDiscardConfirm, setShowDiscardConfirm,
    // Derived
    sectionId,
    assessmentLabel,
    showSubHeaderDropdown,
    showHeaderDropdown,
    sideBySideLayout,
    // Handlers
    handleSave,
    handleCancel,
    handleSectionChange,
    handleSubHeaderChange,
    clearFieldError,
  };
}
